import { motion } from 'framer-motion'

interface FunnelStage {
  label: string
  count: number
  color: string
}

interface CloserFunnelProps {
  stages: FunnelStage[]
  className?: string
}

/** Larguras do topo/base de cada degrau (%) para formato funil centrado. */
function getFunnelWidths(index: number, total: number) {
  const minBottom = 24
  const maxTop = 100
  const step = total > 0 ? (maxTop - minBottom) / total : 0
  const topWidth = maxTop - index * step
  const bottomWidth = maxTop - (index + 1) * step
  return {
    topWidth: Math.max(topWidth, minBottom + 10),
    bottomWidth: Math.max(bottomWidth, minBottom),
  }
}

function trapezoidClip(topWidth: number, bottomWidth: number) {
  const leftTop = (100 - topWidth) / 2
  const rightTop = leftTop + topWidth
  const leftBottom = (100 - bottomWidth) / 2
  const rightBottom = leftBottom + bottomWidth
  return `polygon(${leftTop}% 0%, ${rightTop}% 0%, ${rightBottom}% 100%, ${leftBottom}% 100%)`
}

export function CloserFunnel({ stages, className = '' }: CloserFunnelProps) {
  const n = stages.length
  const maxCount = Math.max(...stages.map((s) => s.count), 1)
  const overallConversion =
    n >= 2 && stages[0].count > 0
      ? Math.round((stages[n - 1].count / stages[0].count) * 100)
      : null

  const segmentHeight = n <= 3 ? 40 : 34

  return (
    <div className={`glass-card flex h-full flex-col p-4 ${className}`.trim()}>
      <div className='flex items-center justify-between gap-2 border-b border-border-subtle pb-3'>
        <h3 className='text-sm font-semibold text-text-primary'>Funil de Conversão</h3>
        {overallConversion != null && (
          <span className='text-xs font-bold text-accent'>{overallConversion}% total</span>
        )}
      </div>

      <div className='flex flex-1 flex-col items-center justify-center py-2'>
        <div className='flex w-full max-w-[240px] flex-col items-center'>
          {stages.map((stage, i) => {
            const { topWidth, bottomWidth } = getFunnelWidths(i, n)
            const clipPath = trapezoidClip(topWidth, bottomWidth)
            const prevCount = i > 0 ? stages[i - 1].count : stage.count
            const conversionRate =
              i > 0 && prevCount > 0 ? Math.round((stage.count / prevCount) * 100) : null
            const volume = stage.count > 0 ? stage.count / maxCount : 0
            const fillAlpha = stage.count > 0 ? 0.22 + volume * 0.28 : 0.14

            return (
              <motion.div
                key={stage.label}
                initial={{ opacity: 0, scale: 0.92 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, delay: i * 0.08, ease: 'easeOut' }}
                className='relative w-full'
                style={{ height: segmentHeight, marginTop: i > 0 ? -1 : 0 }}
              >
                <div
                  className='absolute inset-0 border border-black/[0.06] dark:border-white/[0.08]'
                  style={{
                    clipPath,
                    background: `linear-gradient(180deg, ${stage.color}${Math.round(fillAlpha * 255).toString(16).padStart(2, '0')} 0%, ${stage.color}${Math.round(fillAlpha * 0.75 * 255).toString(16).padStart(2, '0')} 100%)`,
                    boxShadow: `inset 0 1px 0 ${stage.color}30`,
                  }}
                />
                <div
                  className='absolute inset-0 flex flex-col items-center justify-center gap-0 px-1 text-center leading-tight'
                  style={{ clipPath }}
                >
                  <span className='max-w-full truncate text-[9px] font-bold uppercase tracking-wide text-text-muted'>
                    {stage.label}
                  </span>
                  <span className='text-sm font-black' style={{ color: stage.color }}>
                    {stage.count}
                  </span>
                  {conversionRate != null && (
                    <span className='text-[8px] font-semibold text-text-muted'>
                      {conversionRate}% do anterior
                    </span>
                  )}
                </div>
              </motion.div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default CloserFunnel
